import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Schlüssel der gemerkten Projektwahl im `localStorage`.
 *
 * Exportiert, weil ihn außer diesem Context auch der `AuthContext` räumen muss:
 * Die Wahl gilt IMMER nur innerhalb eines Mandanten. Bleibt sie über einen
 * Mandantenwechsel hinweg stehen, fragt die Seitenleiste ein Projekt ab, das
 * es im neuen Mandanten nicht gibt.
 */
export const ACTIVE_PROJECT_STORAGE_KEY = 'pm_active_project_id';
const STORAGE_KEY = ACTIVE_PROJECT_STORAGE_KEY;

function readStored(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeStored(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

interface ActiveProjectValue {
  activeProjectId: string | null;
  setActiveProjectId: (id: string | null) => void;
  /** Wechselt aktives Projekt + navigiert in den Detail-Bereich. */
  selectProject: (id: string, phase?: string) => void;
  /** Wechselt aktives Projekt aus dem TopBar-Dropdown ohne Phase-Wechsel. */
  switchProject: (id: string) => void;
  clearActive: () => void;
}

const ActiveProjectContext = createContext<ActiveProjectValue | null>(null);

export function ActiveProjectProvider({ children }: { children: ReactNode }) {
  const [activeProjectId, setIdState] = useState<string | null>(readStored);

  const setActiveProjectId = useCallback((id: string | null) => {
    setIdState(id);
    writeStored(id);
  }, []);

  const navigate = useNavigate();

  const selectProject = useCallback((id: string, phase?: string) => {
    setActiveProjectId(id);
    navigate(phase ? `/projects/${id}/${phase}` : `/projects/${id}`);
  }, [setActiveProjectId, navigate]);

  const switchProject = useCallback((id: string) => {
    setActiveProjectId(id);
    navigate(`/projects/${id}`);
  }, [setActiveProjectId, navigate]);

  const clearActive = useCallback(() => setActiveProjectId(null), [setActiveProjectId]);

  const value = useMemo<ActiveProjectValue>(() => ({
    activeProjectId,
    setActiveProjectId,
    selectProject,
    switchProject,
    clearActive,
  }), [activeProjectId, setActiveProjectId, selectProject, switchProject, clearActive]);

  return (
    <ActiveProjectContext.Provider value={value}>
      {children}
    </ActiveProjectContext.Provider>
  );
}

export function useActiveProject(): ActiveProjectValue {
  const ctx = useContext(ActiveProjectContext);
  if (!ctx) throw new Error('useActiveProject must be used within ActiveProjectProvider');
  return ctx;
}

/**
 * Synchronisiert die URL-:id mit dem ActiveProject-State.
 * Wird in der ProjectDetailPage gemountet — sobald der User direkt eine
 * Detail-URL aufruft (Bookmark, Deep-Link), uebernimmt der Context die ID.
 */
export function useSyncActiveProjectFromUrl(): void {
  const { id } = useParams<{ id: string }>();
  const { activeProjectId, setActiveProjectId } = useActiveProject();
  useEffect(() => {
    if (id && id !== activeProjectId) {
      setActiveProjectId(id);
    }
  }, [id, activeProjectId, setActiveProjectId]);
}
